/**
 * The platform console, as the shell sees it: its identity, its pages, and
 * what can be started from anywhere.
 *
 * The same shape as an app in ERP-FRONTEND's registry (layouts/apps.js), so
 * the top bar, navigation and command center read alike in both products.
 * There is exactly one app here: the console. It manages ORGANIZATIONS, the
 * module catalog and plans they draw on, and the platform's health — and
 * deliberately has no platform-wide user page, because organization users are
 * read inside their organization (see AdminRoutes.jsx).
 *
 * Every entry is an existing route. Nothing here adds a screen.
 */

export const CONSOLE = {
  key: 'console',
  name: 'Platform console',
  description: 'Organizations, modules, plans and health',
  icon: 'app-admin',
  home: '/',
  sections: [
    {
      label: null,
      items: [
        {
          to: '/',
          label: 'Overview',
          icon: 'overview',
          end: true,
          keywords: 'dashboard home platform summary',
          description: 'Every organization on this deployment, at a glance',
        },
      ],
    },
    {
      label: 'Customers',
      items: [
        {
          to: '/organizations',
          label: 'Organizations',
          icon: 'organization',
          keywords: 'tenants customers companies owners workspaces',
          description: 'Every organization, and who owns it',
        },
        {
          to: '/organizations/new',
          label: 'Create organization',
          icon: 'organization-add',
          end: true,
          keywords: 'new onboard tenant customer owner add',
          description: 'Onboard a customer and its owner',
        },
        {
          to: '/module-requests',
          label: 'Module requests',
          icon: 'module-request',
          badge: 'moduleRequests',
          keywords: 'approve reject pending queue enable',
          description: 'Organizations asking for an optional module',
        },
      ],
    },
    {
      label: 'Catalog',
      items: [
        {
          to: '/modules',
          label: 'Module catalog',
          icon: 'module',
          keywords: 'modules features apps catalog',
          description: 'The ERP areas organizations can switch on',
        },
        {
          to: '/plans',
          label: 'Plans',
          icon: 'plan',
          keywords: 'subscription tiers pricing limits billing',
          description: 'Subscription tiers and their limits',
        },
      ],
    },
    {
      label: 'Platform',
      items: [
        {
          to: '/health',
          label: 'Service health',
          icon: 'health',
          badge: 'health',
          keywords: 'status uptime gateway services down degraded',
          description: "The gateway's view of every backend service",
        },
      ],
    },
  ],
};

CONSOLE.items = CONSOLE.sections.flatMap((section) => section.items);

/**
 * What "+ Create" offers. Creating an organization is its own screen; a module
 * and a plan are created in a dialog on their own page, which opens it when
 * arrived at this way (`state.create`).
 */
export const CREATE_ACTIONS = [
  { to: '/organizations/new', label: 'Organization', icon: 'organization-add', keywords: 'tenant customer onboard owner' },
  { to: '/modules', state: { create: true }, label: 'Module', icon: 'module', keywords: 'catalog feature' },
  { to: '/plans', state: { create: true }, label: 'Plan', icon: 'plan', keywords: 'subscription tier' },
];

export const newLabel = (label) => `New ${label.toLowerCase()}`;

const pathOf = (to) => String(to).split('?')[0];

/**
 * How strongly an item claims a location: the length of the matching prefix,
 * 0 for none. The longest claim wins, so on /organizations/new "Create
 * organization" is lit and "Organizations" is not.
 */
export function matchStrength(item, pathname) {
  const path = pathOf(item.to);
  if (pathname === path) return path.length + 1;
  if (!item.end && path !== '/' && pathname.startsWith(`${path}/`)) return path.length;
  return 0;
}

/** The item a location belongs to, or null. */
export function activeItemIn(items, pathname) {
  let best = null;
  let bestScore = 0;
  for (const item of items) {
    const score = matchStrength(item, pathname);
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return best;
}
